import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { EtablissementResponseDTO } from '../../models/EtablissementResponseDTO';
import { environment } from '../../../environments/environement';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private baseUrl = environment.apiUrl + '/api/v1/etablissements';
  private _etablissement$ = new BehaviorSubject<EtablissementResponseDTO | null>(null);
  readonly etablissement$ = this._etablissement$.asObservable();

  // Default fallback theme
  private readonly DEFAULTS = {
    bgColor: '#f6f9fc',
    primaryColor: '#6c63ff',
    secodaryColor: '#a78bfa',
    bsubmitColor: '#6c63ff',
    beditColor: '#3b82f6',
    bexitColor: '#ef4444',
    titleColor: '#1e1b4b',
    primaryTextColor: '#374151',
    secodaryTextColor: '#6b7280',
  };

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /** Call this right after a successful login */
  loadAndApplyTheme(): Observable<EtablissementResponseDTO | null> {
    if (!isPlatformBrowser(this.platformId)) return of(null);
    const id = localStorage.getItem('etablissementId');
    if (!id) return of(null);

    const token = localStorage.getItem('presence');
    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : new HttpHeaders();

    return this.http
      .get<EtablissementResponseDTO>(`${this.baseUrl}/${id}`, { headers })
      .pipe(
        tap((data) => {
          this._etablissement$.next(data);
          this.applyTheme(data);
          this.cacheTheme(data);
        }),
        catchError((err) => {
          console.error('[ThemeService] Failed to load theme:', err);
          this.restoreCachedTheme();
          return of(null);
        })
      );
  }

  /** Restore from cache (e.g. on page reload without re-login) */
  restoreCachedTheme(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const raw = localStorage.getItem('etablissementTheme');
    if (raw) {
      try {
        const data: EtablissementResponseDTO = JSON.parse(raw);
        this._etablissement$.next(data);
        this.applyTheme(data);
      } catch {
        this.applyDefaults();
      }
    } else {
      this.applyDefaults();
    }
  }

  /** Get current cached etablissement (sync) */
  get currentEtablissement(): EtablissementResponseDTO | null {
    return this._etablissement$.getValue();
  }

  /** Get all establishments (public - no auth required) */
  getAllEstablishments(): Observable<EtablissementResponseDTO[]> {
    return this.http.get<EtablissementResponseDTO[]>(`${this.baseUrl}`);
  }

  buildLogoUrl(logoPath: string | null | undefined): string {
    if (!logoPath) return 'assets/img/monkey.png';
    // Remove wrapping quotes and spaces if the backend serialized it with extra quotes
    const cleanPath = logoPath.replace(/^"|"$/g, '').trim();
    
    if (cleanPath.startsWith('http')) return cleanPath;
    const clean = cleanPath.startsWith('/') ? cleanPath.substring(1) : cleanPath;
    return `${environment.apiUrl}/${clean}`;
  }

  private applyTheme(data: EtablissementResponseDTO): void {
    const root = this.document.documentElement;
    const set = (key: string, val: string | undefined | null, fallback: string) =>
      root.style.setProperty(key, val || fallback);

    set('--color-bg', data.bgColor, this.DEFAULTS.bgColor);
    set('--color-primary', data.primaryColor, this.DEFAULTS.primaryColor);
    set('--color-secondary', data.secodaryColor, this.DEFAULTS.secodaryColor);
    set('--color-btn-submit', data.bsubmitColor, this.DEFAULTS.bsubmitColor);
    set('--color-btn-edit', data.beditColor, this.DEFAULTS.beditColor);
    set('--color-btn-exit', data.bexitColor, this.DEFAULTS.bexitColor);
    set('--color-title', data.titleColor, this.DEFAULTS.titleColor);
    set('--color-text-primary', data.primaryTextColor, this.DEFAULTS.primaryTextColor);
    set('--color-text-secondary', data.secodaryTextColor, this.DEFAULTS.secodaryTextColor);

    // Derived chart colors based on primary
    root.style.setProperty('--color-chart-1', data.primaryColor || this.DEFAULTS.primaryColor);
    root.style.setProperty('--color-chart-2', data.secodaryColor || this.DEFAULTS.secodaryColor);
    root.style.setProperty('--color-chart-3', data.beditColor || this.DEFAULTS.beditColor);
    root.style.setProperty('--color-chart-4', data.bsubmitColor || this.DEFAULTS.bsubmitColor);
  }

  private applyDefaults(): void {
    const root = this.document.documentElement;
    Object.entries(this.DEFAULTS).forEach(([_, val]) => {/* already has fallback */});
    root.style.setProperty('--color-bg', this.DEFAULTS.bgColor);
    root.style.setProperty('--color-primary', this.DEFAULTS.primaryColor);
    root.style.setProperty('--color-secondary', this.DEFAULTS.secodaryColor);
    root.style.setProperty('--color-btn-submit', this.DEFAULTS.bsubmitColor);
    root.style.setProperty('--color-btn-edit', this.DEFAULTS.beditColor);
    root.style.setProperty('--color-btn-exit', this.DEFAULTS.bexitColor);
    root.style.setProperty('--color-title', this.DEFAULTS.titleColor);
    root.style.setProperty('--color-text-primary', this.DEFAULTS.primaryTextColor);
    root.style.setProperty('--color-text-secondary', this.DEFAULTS.secodaryTextColor);
    root.style.setProperty('--color-chart-1', this.DEFAULTS.primaryColor);
    root.style.setProperty('--color-chart-2', this.DEFAULTS.secodaryColor);
    root.style.setProperty('--color-chart-3', this.DEFAULTS.beditColor);
    root.style.setProperty('--color-chart-4', this.DEFAULTS.bsubmitColor);
  }

  private cacheTheme(data: EtablissementResponseDTO): void {
    localStorage.setItem('etablissementTheme', JSON.stringify(data));
  }
}
